import { useCallback, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Rocket } from '@mint-lib/icons';
import { useQuery } from '@mint-lib/query-client';
import { usePlatforms, type Platform } from '@mint-lib/routing-context';
import {
  Card,
  Chip,
  Skeleton,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { Suggestions } from '@mint-sdk/api-recommendations';
import { useAtom } from 'jotai';

import { selectedSuggestionAtom } from '../../../store/index.js';
import type { PlatformPermission } from '../../../types/platfroms.js';
import { PlatformSection } from '../../atoms/Platforms/PlatformSection.jsx';
import { PlatformPermissions } from '../../atoms/Platforms/PlatformsPermissions.jsx';

const AcceptedPlatformsReadOnly = () => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');

  const [selectedSuggestion] = useAtom(selectedSuggestionAtom);

  const { data: platforms, isLoading } = useQuery({
    ...Suggestions.getSuggestionPlatforms({
      urlParams: { id: selectedSuggestion?.id as number },
    }),
    enabled: !!selectedSuggestion?.id,
  });

  const { data = [] } = useQuery({
    ...Suggestions.getSuggestionPermissions({
      urlParams: { id: selectedSuggestion?.id as number },
    }),
    enabled: !!selectedSuggestion?.id,
  });

  const { platformByLabel } = usePlatforms();

  const findPlatformByLabel = useCallback(
    (platforms: Suggestions.SuggestionPlatform[] = []): Platform[] => {
      return platforms.reduce<Platform[]>((acc, platform) => {
        const currentPlatform = platformByLabel[platform.name];

        if (currentPlatform) {
          acc.push(currentPlatform);
        }

        return acc;
      }, []);
    },
    [platformByLabel],
  );

  const automaticApplied: Platform[] = useMemo(
    () => findPlatformByLabel(platforms?.integratedPlatforms),
    [findPlatformByLabel, platforms],
  );

  const manuallyApplied: Platform[] = useMemo(
    () => findPlatformByLabel(platforms?.ghostPlatforms),
    [findPlatformByLabel, platforms],
  );

  const getPlatformPermissionByName = useCallback(
    (data: Suggestions.PlatformPermissionResponseType) => {
      return data.reduce<PlatformPermission[]>((acc, platform) => {
        const currentPlatform = platformByLabel[platform.name];

        if (currentPlatform) {
          acc.push({
            ...currentPlatform,
            children: platform.children,
          });
        }

        return acc;
      }, []);
    },
    [platformByLabel],
  );

  const platformsWithPermissions = useMemo<PlatformPermission[]>(
    () => getPlatformPermissionByName(data),
    [data, getPlatformPermissionByName],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <p className={typographyVariants({ variant: 'h4' })}>
          {t('Suggested platforms recap')}
        </p>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <>
            <PlatformSection
              title={t('Automatic applied platform')}
              platforms={automaticApplied}
              ariaLabel={'automatic-applied'}
            />

            <PlatformSection
              title={t('Manually applied platform')}
              platforms={manuallyApplied}
              ariaLabel={'manually-applied'}
            />

            <PlatformPermissions platforms={platformsWithPermissions} />
          </>
        )}
      </div>
      <Card
        variant="outlined"
        contentClassName="flex gap-4 flex-col w-full"
        className="flex w-full"
      >
        <Chip
          variant="outline"
          color="info"
          className="flex max-w-full shrink-0"
        >
          <Rocket className="fill-info-dark" />
          <TruncateText tooltipClassName="w-full break-all">
            {selectedSuggestion?.journeyName}
          </TruncateText>
        </Chip>
        <div className={typographyVariants({ variant: 'bodyLong01' })}>
          {t(
            'You can decide if you would like to update the current media plan or create a new version of it.',
          )}
        </div>
      </Card>
    </div>
  );
};

export default AcceptedPlatformsReadOnly;
